/**
 * /shift-schedule — расчётное ядро без DOM.
 *
 * Что считает:
 *   • раскладку схемы смен по датам — цикл (2/2, 1/3, 2-2-3, вахта…) от любой
 *     даты «первой смены цикла» в обе стороны, неделю (5/2, 6/1) — по дню
 *     недели, а 5/2 в России — по производственному календарю;
 *   • часы каждой смены по календарным датам: ночная смена 20:00–08:00
 *     делится полуночью, и её утренние часы принадлежат следующему дню и,
 *     на стыке месяцев, следующему месяцу — так ведут табель;
 *   • ночные часы — пересечение с окном ночи (по ТК РФ ст. 96 — 22:00–06:00);
 *   • часы в нерабочие праздники ТК РФ ст. 112 (их оплачивают по ст. 153);
 *   • норму часов по производственному календарю РФ и отклонение от неё за
 *     учётный период (суммированный учёт, ст. 104); отпуск и больничный
 *     уменьшают норму на часы рабочих дней, которые они занимают
 *     (приказ Минздравсоцразвития № 588н, п. 3);
 *   • сдвиг бригад, покрытие дня и ночи, очередь дежурств;
 *   • файлы: .ics (каждая смена — событие со временем) и CSV.
 *
 * Перерыв внутри смены (обед) не входит в отработанное время и считается
 * стоящим ПОСЕРЕДИНЕ смены: у ночи 20:00–08:00 с часом перерыва он ложится на
 * 01:30–02:30, и ночных часов выходит 7, а не 8. Где перерыв на самом деле,
 * ядро не знает — правило названо в docs/why/shift-schedule.md.
 *
 * Производственный календарь — общий модуль public/js/ru-work-calendar.js
 * (данные постановлений Правительства РФ о переносе выходных 2025–2027);
 * помесячные нормы сверены с таблицами КонсультантПлюс в
 * tests/js/shift-schedule-core.test.mjs. Года без постановления ядро не
 * выдумывает: норма у него — null. Скрипт календаря грузится ДО этого файла.
 *
 * Время — целые минуты от 1970-01-01 00:00 по стенным часам (без часовых
 * поясов): у смены нет часового пояса, есть «с 08:00 до 20:00».
 */
(function (root) {
'use strict';

var DAY = 1440;

function pad(n) { return (n < 10 ? '0' : '') + n; }
function mod(a, n) { return ((a % n) + n) % n; }

/* ── даты как номера дней ─────────────────────────────────────────────── */
function dnOf(y, m, d) { return Math.floor(Date.UTC(y, m - 1, d) / 864e5); }
function ymdOf(dn) {
    var t = new Date(dn * 864e5);
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}
function isoOf(dn) { var o = ymdOf(dn); return o.y + '-' + pad(o.m) + '-' + pad(o.d); }
function parseIso(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''));
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    var dn = dnOf(y, mo, d), c = ymdOf(dn);
    return (c.y === y && c.m === mo && c.d === d) ? dn : null;
}
/** 0 = понедельник … 6 = воскресенье (1970-01-01 — четверг). */
function dow(dn) { return mod(dn + 3, 7); }
function daysInMonth(y, m) { return dnOf(y, m + 1, 1) - dnOf(y, m, 1); }
function monthRange(y, m) { var a = dnOf(y, m, 1); return [a, a + daysInMonth(y, m) - 1]; }
function hm(min) { min = mod(Math.round(min), DAY); return pad(Math.floor(min / 60)) + ':' + pad(min % 60); }
function parseHm(s) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(s || '').trim());
    if (!m) return null;
    var h = +m[1], mi = +m[2];
    return (h < 24 && mi < 60) ? h * 60 + mi : null;
}

/* ── производственный календарь РФ ────────────────────────────────────── */
/* Данные и правила — общий модуль public/js/ru-work-calendar.js (с 23.10.2026:
   его же читают /vacation-schedule и табель). Здесь — прежние имена API ядра,
   чтобы app.js и тест не менялись: RU_CAL, ruWorking, ruMonthNorm… */
var Cal = root.RuWorkCalendar || (typeof require === 'function' ? require('../ru-work-calendar.js') : null);
if (!Cal) throw new Error('RuWorkCalendar missing: load js/ru-work-calendar.js before shift-schedule/core.js');
var RU_CAL = Cal.DATA;
var RU_HOLIDAYS = Cal.HOLIDAYS;
function ruKnown(y) { return Cal.known(y); }
/** true / false — рабочий ли день по производственному календарю; null — года нет. */
function ruWorking(dn) { return Cal.isWorking(dn); }
function ruShort(dn) { return Cal.isShort(dn); }
function ruHoliday(dn) { return Cal.isHoliday(dn); }
/** Нерабочий день календаря: выходной, праздник или перенос. null — года нет. */
function ruDayOff(dn) { return Cal.isDayOff(dn); }
/** Норма дня в минутах при недельной норме `weekly` часов; null — года нет. */
function ruNormDay(dn, weekly) { return Cal.normDay(dn, weekly); }
function ruMonthNorm(y, m, weekly) { return Cal.monthNorm(y, m, weekly); }

/* ── схемы ─────────────────────────────────────────────────────────────── */
/* D — дневная, N — ночная, F — сутки, S — короткая смена, O — выходной. */
var WORK = { D: 1, N: 1, F: 1, S: 1 };
var KIND_DEFAULTS = {
    D: { s: 480, l: 720, b: 0 },
    N: { s: 1200, l: 720, b: 0 },
    F: { s: 480, l: 1440, b: 0 },
    S: { s: 540, l: 240, b: 0 },
};
function rep(ch, n) { var s = ''; for (var i = 0; i < n; i++) s += ch; return s; }
var PRESETS = [
    { id: '2-2', seq: 'DDOO' },
    { id: '1-1', seq: 'DO' },
    { id: '3-3', seq: 'DDDOOO' },
    { id: '4-4', seq: 'DDDDOOOO' },
    { id: '5-2', seq: 'DDDDDOO', week: true, cal: true, kinds: { D: { s: 540, l: 540, b: 60 } } },
    { id: '6-1', seq: 'DDDDDDO', week: true, kinds: { D: { s: 540, l: 480, b: 60 } } },
    { id: '4-2', seq: 'DDDDOO' },
    { id: '2-2-3', seq: 'DDOODDDOODDOOO' },
    { id: '1-3', seq: 'FOOO' },
    { id: '1-2', seq: 'FOO' },
    { id: 'dnoo', seq: 'DNOO' },
    { id: '2d2n', seq: 'DDNNOOOO' },
    { id: 'w15', seq: rep('D', 15) + rep('O', 15), kinds: { D: { s: 480, l: 720, b: 60 } } },
    { id: 'w30', seq: rep('D', 30) + rep('O', 30), kinds: { D: { s: 480, l: 720, b: 60 } } },
];
function preset(id) {
    for (var i = 0; i < PRESETS.length; i++) if (PRESETS[i].id === id) return PRESETS[i];
    return null;
}
function cleanSeq(seq) {
    var s = String(seq || '').toUpperCase().replace(/[^DNFSO]/g, '');
    if (s.length > 62) s = s.slice(0, 62);
    return s || 'DO';
}
function kindSet(seq) {
    var out = [];
    ['D', 'N', 'F', 'S'].forEach(function (k) { if (seq.indexOf(k) >= 0) out.push(k); });
    return out;
}

/**
 * Нормализованная конфигурация расписания.
 *   seq, week (по дням недели), cal ('ru' — 5/2 по календарю), anchor (номер дня),
 *   kinds {D:{s,l,b}…}, crews [{o: сдвиг}], marks {crew: {iso: тип}}
 */
function normalize(cfg) {
    cfg = cfg || {};
    var seq = cleanSeq(cfg.seq);
    var kinds = {};
    Object.keys(KIND_DEFAULTS).forEach(function (k) {
        var d = KIND_DEFAULTS[k], v = (cfg.kinds && cfg.kinds[k]) || {};
        var s = isFinite(v.s) ? mod(Math.round(v.s), DAY) : d.s;
        var l = isFinite(v.l) ? Math.max(15, Math.min(48 * 60, Math.round(v.l))) : d.l;
        var b = isFinite(v.b) ? Math.max(0, Math.min(l - 15, Math.round(v.b))) : d.b;
        kinds[k] = { s: s, l: l, b: b };
    });
    var crews = Array.isArray(cfg.crews) && cfg.crews.length ? cfg.crews.slice(0, 12) : [{ o: 0 }];
    return {
        seq: seq,
        week: !!cfg.week && seq.length === 7,
        cal: cfg.cal === 'ru' ? 'ru' : null,
        anchor: isFinite(cfg.anchor) ? Math.round(cfg.anchor) : dnOf(2026, 1, 1),
        kinds: kinds,
        crews: crews.map(function (c) { return { o: isFinite(c && c.o) ? Math.round(c.o) : 0 }; }),
        marks: cfg.marks || {},
        holidays: cfg.holidays === 'ru' ? 'ru' : null,
        night: [isFinite(cfg.nightFrom) ? mod(cfg.nightFrom, DAY) : 1320, isFinite(cfg.nightTo) ? mod(cfg.nightTo, DAY) : 360],
    };
}

/** Автосдвиг бригад: i-я бригада начинает цикл на round(i·L/N) дней позже. */
function autoOffsets(seqLen, n) {
    var out = [];
    for (var i = 0; i < n; i++) out.push(Math.round(i * seqLen / n) % Math.max(1, seqLen));
    return out;
}

/** Что стоит в схеме в этот день, без пометок: буква и признак «короткий по календарю». */
function baseKind(c, crew, dn) {
    var cr = c.crews[crew] || c.crews[0];
    if (c.week) {
        if (c.cal === 'ru') {
            var w = ruWorking(dn);
            if (w !== null) return { k: w ? c.seq.charAt(0) === 'O' ? 'D' : c.seq.charAt(0) : 'O', short: w && ruShort(dn) };
        }
        return { k: c.seq.charAt(mod(dow(dn) - cr.o, 7)), short: false };
    }
    return { k: c.seq.charAt(mod(dn - c.anchor - cr.o, c.seq.length)), short: false };
}

/* Пометки: vac — отпуск, sick — больничный, off — выходной вместо смены
   (отгул, обмен), extra — дополнительная смена. */
var MARKS = { vac: 1, sick: 1, off: 1, extra: 1 };
function markOf(c, crew, dn) {
    var m = c.marks && c.marks[crew];
    var v = m ? m[isoOf(dn)] : null;
    return MARKS[v] ? v : null;
}
function extraKind(c) {
    var ks = kindSet(c.seq);
    return ks.length ? ks[0] : 'D';
}

/** День бригады: {k, base, mark, short}. k — что фактически стоит с учётом пометки. */
function cell(c, crew, dn) {
    var b = baseKind(c, crew, dn), mark = markOf(c, crew, dn), k = b.k;
    if (mark === 'vac' || mark === 'sick' || mark === 'off') k = 'O';
    else if (mark === 'extra' && !WORK[k]) k = extraKind(c);
    return { k: k, base: b.k, mark: mark, short: b.short && WORK[k] ? true : false };
}

/** Отрезки работы смены в минутах эпохи: перерыв — посередине смены. */
function segments(c, dn, k, short) {
    var K = c.kinds[k];
    var l = K.l - (short ? 60 : 0);
    if (l < 15) l = 15;
    var s = dn * DAY + K.s, e = s + l, b = Math.min(K.b, l - 15);
    if (b <= 0) return { s: s, e: e, segs: [[s, e]] };
    var b0 = s + Math.round((l - b) / 2);
    return { s: s, e: e, segs: [[s, b0], [b0 + b, e]] };
}

function overlap(a0, a1, b0, b1) { var x = Math.max(a0, b0), y = Math.min(a1, b1); return y > x ? y - x : 0; }
function nightOverlap(a, b, night) {
    var ns = night[0], ne = night[1], sum = 0;
    if (ns === ne) return 0;
    for (var d = Math.floor(a / DAY) - 1; d <= Math.floor(b / DAY) + 1; d++) {
        var w0 = d * DAY + ns, w1 = ns < ne ? d * DAY + ne : (d + 1) * DAY + ne;
        sum += overlap(a, b, w0, w1);
    }
    return sum;
}

/*
 * Перевод часов. Смена задана стенными часами («с 20:00 до 08:00»), а ночь
 * перевода на летнее время короче на час, на зимнее — длиннее: в Германии и в
 * США такая ночная смена — 11 и 13 часов, и табель платит по факту. Сколько
 * часов прошло на самом деле, знает только часовой пояс человека, поэтому ядро
 * спрашивает его функцией `offsetAt(минуты стенных часов)` → смещение от UTC в
 * минутах (как Date#getTimezoneOffset); без неё — стенные часы как есть. В
 * России перевода нет, и поправка всегда ноль.
 */
function utcOf(min, offsetAt) { return min + offsetAt(min); }
/** Момент перевода внутри [a, b] (минуты стенных часов) или null. */
function dstPoint(a, b, offsetAt) {
    var oa = offsetAt(a), ob = offsetAt(b);
    if (oa === ob) return null;
    var lo = a, hi = b;
    while (hi - lo > 1) { var mid = Math.floor((lo + hi) / 2); if (offsetAt(mid) === oa) lo = mid; else hi = mid; }
    return hi;
}
/**
 * Сколько работы в отрезках смены внутри [A, B): {work, night, holiday, dst}.
 * dst — поправка перевода часов в минутах (−60 весной, +60 осенью, 0 обычно);
 * она уже внутри work и, если перевод пришёлся на ночное окно, внутри night.
 */
function workOf(c, segs, A, B, offsetAt) {
    var r = { work: 0, night: 0, holiday: 0, dst: 0 };
    segs.forEach(function (sg) {
        var a = Math.max(sg[0], A), b = Math.min(sg[1], B);
        if (b <= a) return;
        var wall = b - a, night = nightOverlap(a, b, c.night), d = 0;
        if (offsetAt) {
            d = (utcOf(b, offsetAt) - utcOf(a, offsetAt)) - wall;
            if (d) {
                var p = dstPoint(a, b, offsetAt);
                if (p !== null && nightOverlap(p - 1, p + 1, c.night) === 2) night = Math.max(0, night + d);
            }
        }
        r.work += wall + d;
        r.night += night;
        r.dst += d;
        if (c.holidays === 'ru') {
            for (var dd = Math.floor(a / DAY); dd * DAY < b; dd++) {
                if (ruHoliday(dd)) r.holiday += overlap(a, b, dd * DAY, (dd + 1) * DAY);
            }
        }
    });
    return r;
}

/** Все смены бригады, чьё время задевает дни [from, to]. */
function shifts(c, crew, from, to) {
    var out = [];
    var maxLen = 0;
    Object.keys(c.kinds).forEach(function (k) { if (c.kinds[k].l > maxLen) maxLen = c.kinds[k].l; });
    var back = Math.ceil(maxLen / DAY) + 1;
    for (var dn = from - back; dn <= to; dn++) {
        var x = cell(c, crew, dn);
        if (!WORK[x.k]) continue;
        var sg = segments(c, dn, x.k, x.short);
        if (sg.e <= from * DAY && dn < from) continue;
        out.push({ dn: dn, k: x.k, s: sg.s, e: sg.e, segs: sg.segs, mark: x.mark, short: x.short });
    }
    return out;
}

/**
 * Итоги бригады за дни [from, to]:
 *   shifts (смены, начавшиеся в периоде), byKind, min (отработано, по датам),
 *   night, holiday (минуты в праздники ст. 112, если holidays='ru'),
 *   norm (минуты, null — нет календаря), dev (min − norm), absent (дней отпуска и больничного).
 */
function stats(c, crew, from, to, opts) {
    opts = opts || {};
    var weekly = isFinite(opts.weekly) ? opts.weekly : 40;
    var r = { shifts: 0, byKind: { D: 0, N: 0, F: 0, S: 0 }, min: 0, night: 0, holiday: 0, norm: null, dev: null, absent: 0, extra: 0 };
    var A = from * DAY, B = (to + 1) * DAY;
    shifts(c, crew, from, to).forEach(function (sh) {
        if (sh.dn >= from && sh.dn <= to) {
            r.shifts++;
            r.byKind[sh.k]++;
            if (sh.mark === 'extra') r.extra++;
        }
        var w = workOf(c, sh.segs, A, B, opts.offsetAt);
        r.min += w.work;
        r.night += w.night;
        r.holiday += w.holiday;
    });
    if (c.holidays === 'ru') {
        var norm = 0, known = true;
        for (var dn = from; dn <= to; dn++) {
            var nd = ruNormDay(dn, weekly);
            if (nd === null) { known = false; break; }
            var mk = markOf(c, crew, dn);
            if (mk === 'vac' || mk === 'sick') { r.absent++; continue; }
            norm += nd;
        }
        if (known) { r.norm = norm; r.dev = r.min - norm; }
    } else {
        for (var dn2 = from; dn2 <= to; dn2++) {
            var mk2 = markOf(c, crew, dn2);
            if (mk2 === 'vac' || mk2 === 'sick') r.absent++;
        }
    }
    return r;
}

/** Учётный период, в который входит месяц: month | quarter | half | year. */
function periodRange(y, m, kind) {
    var m0 = m, m1 = m;
    if (kind === 'quarter') { m0 = Math.floor((m - 1) / 3) * 3 + 1; m1 = m0 + 2; }
    else if (kind === 'half') { m0 = m <= 6 ? 1 : 7; m1 = m0 + 5; }
    else if (kind === 'year') { m0 = 1; m1 = 12; }
    return { m0: m0, m1: m1, from: dnOf(y, m0, 1), to: monthRange(y, m1)[1] };
}

/** Сколько смен цикла начинается раньше, чем кончилась предыдущая (часы посчитались бы дважды). */
function overlaps(c, crew) {
    var from = c.anchor, to = c.anchor + Math.max(14, c.seq.length * 2), n = 0;
    var list = shifts(c, crew, from, to);
    for (var i = 1; i < list.length; i++) if (list[i].s < list[i - 1].e) n++;
    return n;
}

/** Кто на смене в день: {D, N, F, S} — число бригад, начавших смену этого вида в этот день. */
function coverage(c, dn) {
    var r = { D: 0, N: 0, F: 0, S: 0 };
    for (var i = 0; i < c.crews.length; i++) {
        var k = cell(c, i, dn).k;
        if (WORK[k]) r[k]++;
    }
    return r;
}

/** Ближайшая смена, которая ещё не кончилась к моменту `nowMin` (минуты эпохи). */
function nextShift(c, crew, nowMin, horizonDays) {
    var d0 = Math.floor(nowMin / DAY);
    var list = shifts(c, crew, d0, d0 + (horizonDays || 400));
    for (var i = 0; i < list.length; i++) if (list[i].e > nowMin) return list[i];
    return null;
}

/* ── дежурства ─────────────────────────────────────────────────────────── */
/**
 * Очередь дежурств: rule = daily | workdays | weekly | every (каждые `every` дней).
 * workdays — по рабочим дням: в России года с календарём — по нему, иначе все
 * дни, кроме выходных `weekend` (номера дней недели, по умолчанию сб и вс).
 * Возвращает индекс человека или −1 (в этот день дежурства нет).
 */
function isWorkday(dn, calRu, weekend) {
    if (calRu) { var w = ruWorking(dn); if (w !== null) return w; }
    return (weekend || [5, 6]).indexOf(dow(dn)) < 0;
}
function dutyIndex(d, dn) {
    var n = d.n | 0;
    if (n <= 0) return -1;
    var st = d.start;
    if (d.rule === 'weekly') return mod(Math.floor((dn - (st - dow(st))) / 7), n);
    if (d.rule === 'every') return mod(Math.floor((dn - st) / Math.max(1, d.every | 0)), n);
    if (d.rule === 'workdays') {
        var ru = d.cal === 'ru', we = d.weekend;
        if (!isWorkday(dn, ru, we)) return -1;
        var cnt = 0, x;
        if (dn >= st) { for (x = st; x < dn; x++) if (isWorkday(x, ru, we)) cnt++; }
        else { for (x = dn; x < st; x++) if (isWorkday(x, ru, we)) cnt--; }
        return mod(cnt, n);
    }
    return mod(dn - st, n);
}

/* ── файлы ─────────────────────────────────────────────────────────────── */
function icsStamp(min) {
    var dn = Math.floor(min / DAY), o = ymdOf(dn), t = mod(min, DAY);
    return o.y + pad(o.m) + pad(o.d) + 'T' + pad(Math.floor(t / 60)) + pad(t % 60) + '00';
}
function icsEscape(s) { return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n'); }
/* RFC 5545 §3.1: строка не длиннее 75 октетов UTF-8, продолжение — с пробела. */
function icsFold(line) {
    var out = [], cur = '', bytes = 0;
    for (var i = 0; i < line.length; i++) {
        var ch = line.charAt(i), code = line.charCodeAt(i);
        if (code >= 0xD800 && code <= 0xDBFF && i + 1 < line.length) { ch += line.charAt(++i); }
        var b = encodeURIComponentLen(ch);
        if (bytes + b > (out.length ? 74 : 75)) { out.push(cur); cur = ''; bytes = 0; }
        cur += ch; bytes += b;
    }
    out.push(cur);
    return out.join('\r\n ');
}
function encodeURIComponentLen(ch) {
    var c = ch.codePointAt ? ch.codePointAt(0) : ch.charCodeAt(0);
    return c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
}
/**
 * events: [{s, e, title, uid, desc?}] — минуты эпохи по стенным часам;
 *         [{allDay: true, dn, title, uid}] — событие на весь день (дежурство).
 * Время «плавающее» (без TZID): телефон ставит смену 08:00 в 08:00 там, где он есть.
 */
function ics(events, opts) {
    opts = opts || {};
    var now = opts.now ? new Date(opts.now) : new Date();
    var stamp = now.getUTCFullYear() + pad(now.getUTCMonth() + 1) + pad(now.getUTCDate()) + 'T' +
        pad(now.getUTCHours()) + pad(now.getUTCMinutes()) + pad(now.getUTCSeconds()) + 'Z';
    var L = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Timbrica//Shift schedule//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH'];
    if (opts.name) L.push('X-WR-CALNAME:' + icsEscape(opts.name));
    events.forEach(function (ev) {
        L.push('BEGIN:VEVENT');
        L.push('UID:' + ev.uid);
        L.push('DTSTAMP:' + stamp);
        if (ev.allDay) {
            /* Дежурство — весь день: DATE без времени, конец — следующий день (RFC 5545 §3.6.1). */
            L.push('DTSTART;VALUE=DATE:' + icsStamp(ev.dn * DAY).slice(0, 8));
            L.push('DTEND;VALUE=DATE:' + icsStamp((ev.dn + 1) * DAY).slice(0, 8));
        } else {
            L.push('DTSTART:' + icsStamp(ev.s));
            L.push('DTEND:' + icsStamp(ev.e));
        }
        L.push('SUMMARY:' + icsEscape(ev.title));
        if (ev.desc) L.push('DESCRIPTION:' + icsEscape(ev.desc));
        L.push('TRANSP:OPAQUE');
        /* Напоминание (RFC 5545 §3.6.6): за `alarm` минут до начала. Календарь
           iPhone и Outlook его выполняют; Google при импорте ставит свои. У
           ACTION:DISPLAY DESCRIPTION обязателен — без него iOS выбрасывает тревогу. */
        if (ev.alarm > 0) {
            L.push('BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + icsEscape(ev.title), 'TRIGGER:-PT' + Math.round(ev.alarm) + 'M', 'END:VALARM');
        }
        L.push('END:VEVENT');
    });
    L.push('END:VCALENDAR');
    return L.map(icsFold).join('\r\n') + '\r\n';
}

/** CSV: разделитель ';' при десятичной запятой (так его открывает Excel с такой локалью). */
function csv(rows, decimal) {
    var sep = decimal === ',' ? ';' : ',';
    return rows.map(function (r) {
        return r.map(function (v) {
            var s = typeof v === 'number' ? String(v).replace('.', decimal === ',' ? ',' : '.') : String(v == null ? '' : v);
            return (s.indexOf(sep) >= 0 || /["\n\r]/.test(s)) ? '"' + s.replace(/"/g, '""') + '"' : s;
        }).join(sep);
    }).join('\r\n') + '\r\n';
}

var api = {
    DAY: DAY, dnOf: dnOf, ymdOf: ymdOf, isoOf: isoOf, parseIso: parseIso, dow: dow,
    daysInMonth: daysInMonth, monthRange: monthRange, hm: hm, parseHm: parseHm, mod: mod,
    RU_CAL: RU_CAL, RU_HOLIDAYS: RU_HOLIDAYS, ruKnown: ruKnown, ruWorking: ruWorking, ruShort: ruShort,
    ruHoliday: ruHoliday, ruDayOff: ruDayOff, ruNormDay: ruNormDay, ruMonthNorm: ruMonthNorm,
    KIND_DEFAULTS: KIND_DEFAULTS, PRESETS: PRESETS, preset: preset, cleanSeq: cleanSeq, kindSet: kindSet,
    WORK: WORK, normalize: normalize, autoOffsets: autoOffsets, cell: cell, segments: segments,
    shifts: shifts, workOf: workOf, stats: stats, periodRange: periodRange, coverage: coverage, overlaps: overlaps, nextShift: nextShift,
    nightOverlap: nightOverlap, dutyIndex: dutyIndex, isWorkday: isWorkday, ics: ics, csv: csv,
};
root.ShiftSchedule = api;
if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
